import { useMemo, useState } from "react";
import { addHours, addMinutes, format, startOfDay } from "date-fns";
import { createFileRoute } from "@tanstack/react-router";
import type {
  SchedulerEvent,
  SchedulerEventInput,
  SchedulerResource,
} from "@/components/scheduler";
import { Scheduler } from "@/components/scheduler";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/scheduler-demo")({
  component: SchedulerDemoPage,
});

function SchedulerDemoPage() {
  const resources = useMemo<SchedulerResource[]>(
    () => [
      {
        id: "1",
        title: "Operating Room A",
        subtitle: "Cardiology",
        color: "#0ea5e9",
      },
      {
        id: "2",
        title: "Operating Room B",
        subtitle: "Orthopedics",
        color: "#9333ea",
      },
      {
        id: "3",
        title: "Operating Room C",
        subtitle: "Outpatient",
        color: "#f97316",
      },
    ],
    [],
  );

  const createInitialEvents = () => {
    const baseDay = startOfDay(new Date());
    return [
      {
        id: "evt-1",
        title: "Pre-op Assessment",
        start: addHours(addMinutes(baseDay, 30), 7),
        end: addHours(addMinutes(baseDay, 15), 8),
        resourceId: "1",
      },
      {
        id: "evt-2",
        title: "Spinal Fusion",
        start: addHours(addMinutes(baseDay, 45), 9),
        end: addHours(addMinutes(baseDay, 0), 12),
        resourceId: "2",
      },
      {
        id: "evt-3",
        title: "Post-op Recovery",
        start: addHours(addMinutes(baseDay, 15), 13),
        end: addHours(addMinutes(baseDay, 30), 15),
        resourceId: "3",
      },
    ];
  };

  const [events, setEvents] = useState<SchedulerEvent[]>(createInitialEvents);

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const handleEventCreate = (eventInput: SchedulerEventInput) => {
    const resource = resources.find(
      (item) => item.id === eventInput.resourceId,
    );
    const identifier =
      typeof crypto !== "undefined" && crypto?.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
    const newEvent: SchedulerEvent = {
      id: identifier,
      title: resource ? `${resource.title} booking` : `New reservation`,
      start: eventInput.start,
      end: eventInput.end,
      resourceId: eventInput.resourceId,
    };

    setEvents((prev) => [...prev, newEvent]);
    setSelectedEventId(newEvent.id);
  };

  const handleEventUpdate = (updatedEvent: SchedulerEvent) => {
    setEvents((prev) =>
      prev.map((event) =>
        event.id === updatedEvent.id
          ? {
              ...event,
              start: updatedEvent.start,
              end: updatedEvent.end,
              resourceId: updatedEvent.resourceId,
            }
          : event,
      ),
    );
    setSelectedEventId(updatedEvent.id);
  };

  const handleEventSelect = (event: SchedulerEvent) => {
    setSelectedEventId(event.id);
  };

  const handleResetSchedule = () => {
    setEvents(createInitialEvents());
    setSelectedEventId(null);
  };

  const selectedEvent =
    events.find((event) => event.id === selectedEventId) ?? null;

  return (
    <div className="min-h-screen bg-slate-950 py-16">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-6">
        <header className="rounded-3xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 p-[1px] shadow-xl">
          <div className="rounded-[calc(theme(borderRadius.3xl)-1px)] bg-slate-900/80 p-10">
            <div className="flex flex-col gap-4 text-white lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-2">
                <p className="text-sm uppercase tracking-[0.4em] text-blue-200">
                  Interactive prototype
                </p>
                <h1 className="text-3xl font-semibold lg:text-4xl">
                  Scheduler playground
                </h1>
                <p className="text-base text-blue-100/80 lg:max-w-xl">
                  Drag events to reschedule, resize them to adjust durations, or
                  select an empty slot to create a new booking.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                  onClick={handleResetSchedule}
                >
                  Reset sample events
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="min-w-0">
            <Scheduler
              events={events}
              resources={resources}
              onEventCreate={handleEventCreate}
              onEventUpdate={handleEventUpdate}
              onEventSelect={handleEventSelect}
            />
          </section>

          <aside className="flex flex-col gap-6 rounded-3xl border border-white/5 bg-slate-900/70 p-6 text-slate-100">
            <div>
              <h2 className="text-lg font-semibold">Resource legend</h2>
              <ul className="mt-3 space-y-3">
                {resources.map((resource) => (
                  <li
                    key={resource.id}
                    className="flex items-center gap-3 rounded-xl border border-white/5 bg-slate-900/60 p-3"
                  >
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: resource.color }}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">{resource.title}</span>
                      {resource.subtitle ? (
                        <span className="text-sm text-slate-400">
                          {resource.subtitle}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold">Event details</h2>
              {selectedEvent ? (
                <div className="rounded-2xl border border-white/5 bg-white/5 p-4 text-sm">
                  <p className="text-lg font-semibold text-white">
                    {selectedEvent.title}
                  </p>
                  <p className="text-slate-300">
                    {format(selectedEvent.start, "MMM d, yyyy • p")} —{" "}
                    {format(selectedEvent.end, "p")}
                  </p>
                  {selectedEvent.resourceId ? (
                    <p className="pt-2 text-slate-400">
                      Assigned to{" "}
                      {resources.find(
                        (item) => item.id === selectedEvent.resourceId,
                      )?.title ?? "Unassigned"}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="rounded-2xl border border-dashed border-white/10 p-4 text-sm text-slate-400">
                  Select an event to inspect its details.
                </p>
              )}
            </div>

            <div className="space-y-2 text-sm text-slate-400">
              <h2 className="text-lg font-semibold text-white">Tips</h2>
              <ul className="space-y-2">
                <li>
                  Drag an event to move it to a different resource or time slot.
                </li>
                <li>Resize the top or bottom edge to adjust the duration.</li>
                <li>Select an empty slot to instantly create a new booking.</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
